import * as Logger from './logger.js';
import { HSComponent } from './component.js';
import { HSPipelineSandbox } from './pipeline-sandbox.js';
import { escapeHtmlAttribute } from './utils.js';
import './toolbar-button.js';
import styles from './service-monitor.css';
import pipelineStyles from './pipeline-sandbox.css';
import template from './service-monitor.html';

export class HSServiceMonitor extends HSComponent {
    #services = [];
    #selectedService = null;
    #pipelineViewer = null;

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(pipelineStyles);
        this.loadStyles(styles);
        this.#bindEvents();
        this.#fetchServices();
    }

    onUnload() {
        super.onUnload();
        this.#removePipelineViewer();
    }

    #bindEvents() {
        const sd = this.shadowRoot;
        this.onEvent('click', () => this.#close(), sd.querySelector('.ps-backdrop'));
        this.onEvent('click', () => this.#close(), sd.getElementById('sm-close-btn'));
        this.onEvent('click', () => this.#fetchServices(), sd.getElementById('sm-refresh-btn'));
    }

    async #withLoading(fn) {
        const overlay = this.shadowRoot.getElementById('sm-loading-overlay');
        overlay?.classList.remove('hidden');
        try {
            return await fn();
        } finally {
            overlay?.classList.add('hidden');
        }
    }

    async #fetchServices() {
        await this.#withLoading(async () => {
            try {
                const res = await this.fetch('/elm/services');
                this.#services = Array.isArray(res) ? res : [];
                this.#renderServiceTable();
            } catch (err) {
                Logger.error('Failed to fetch ELM services: ', err);
                this.#services = [];
                this.#renderServiceTable();
            }
        });
    }

    #renderServiceTable() {
        const container = this.shadowRoot.getElementById('sm-service-table');
        if (!this.#services.length) {
            container.innerHTML = '<div class="sm-empty-state">No ELM services discovered</div>';
            return;
        }

        const rows = this.#services.map(s => {
            const badge = s.isSandbox ? ' <span class="sm-badge sm-badge-sandbox">sandbox</span>' : '';
            const imageShort = s.image.split('/').pop() || s.image;
            return `<tr class="sm-service-row" data-name="${this.#esc(s.name)}">
                <td><a href="#" class="sm-service-link" data-name="${this.#esc(s.name)}">${this.#esc(s.name)}${badge}</a></td>
                <td class="sm-image-cell" title="${this.#esc(s.image)}">${this.#esc(imageShort)}</td>
            </tr>`;
        }).join('');

        container.innerHTML = `<table class="ps-table">
            <thead><tr><th>Service</th><th>Image</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>`;

        for (const link of container.querySelectorAll('.sm-service-link')) {
            this.onEvent('click', (_, ev) => {
                ev.preventDefault();
                this.#openService(link.dataset.name);
            }, link);
        }
    }

    #openService(serviceName) {
        this.#selectedService = serviceName;
        this.shadowRoot.getElementById('sm-service-list').classList.add('hidden');
        const pipelineView = this.shadowRoot.getElementById('sm-pipeline-view');
        pipelineView.classList.remove('hidden');

        this.#removePipelineViewer();
        this.#pipelineViewer = document.createElement('hs-pipeline-sandbox');
        this.#pipelineViewer.readOnly = true;
        this.#pipelineViewer.baseUrl = `/elm/services/${encodeURIComponent(serviceName)}/api`;
        // Override close to return to service list instead of closing the monitor
        this.onEvent('hs-close-pipeline-sandbox', () => this.#backToServiceList(), this.#pipelineViewer);
        pipelineView.appendChild(this.#pipelineViewer);
    }

    #backToServiceList() {
        this.#removePipelineViewer();
        this.shadowRoot.getElementById('sm-pipeline-view').classList.add('hidden');
        this.shadowRoot.getElementById('sm-service-list').classList.remove('hidden');
        this.#selectedService = null;
    }

    #removePipelineViewer() {
        if (this.#pipelineViewer) {
            this.#pipelineViewer.remove();
            this.#pipelineViewer = null;
        }
    }

    #close() {
        this.#removePipelineViewer();
        this.emit({ type: 'hs-close-service-monitor' });
    }

    #esc(str) {
        return escapeHtmlAttribute(str);
    }
}

customElements.define('hs-service-monitor', HSServiceMonitor);
